import { useState, useEffect, useRef } from "react";
import API from "@/lib/api";
import { ArrowsClockwise, Buildings, Users, CurrencyDollar, ChartLineUp, TrendUp, HandCoins, Vault } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export default function AdminDashboard() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const didFetch = useRef(false);

  // `fresh` bypasses the server's short summary cache — the Refresh button
  // should always show current numbers, not the cached copy.
  const fetchSummary = async (fresh = false) => {
    setLoading(true);
    try {
      const { data } = await API.get("/admin/reports/summary", {
        params: fresh ? { fresh: 1 } : undefined,
      });
      setSummary(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;
    fetchSummary();
  }, []);

  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading dashboard...</div>;

  const metrics = [
    { label: "Total Brands", value: summary?.total_brands || 0, icon: Buildings, color: "text-[#002FA7]" },
    { label: "Total Publishers", value: summary?.total_publishers || 0, icon: Users, color: "text-emerald-600" },
    { label: "Active Assignments", value: summary?.total_assignments || 0, icon: ChartLineUp, color: "text-amber-600" },
    { label: "Total Actions", value: summary?.total_actions || 0, icon: CurrencyDollar, color: "text-slate-700" },
  ];

  const fmt = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const margin = summary?.margin || { today: {}, mtd: {}, all_time: {} };

  const MarginCard = ({ title, period, data, accent }) => (
    <div
      className="border border-slate-200 rounded-md p-5 bg-white relative overflow-hidden"
      data-testid={`margin-card-${period}`}
    >
      <div className={`absolute top-0 left-0 h-full w-0.5 ${accent}`} />
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold">{title}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">{data?.actions || 0} conversions</p>
        </div>
        <TrendUp size={20} className="text-slate-400" weight="duotone" />
      </div>

      <div className="flex items-baseline gap-2 mb-3">
        <span className="font-mono text-3xl font-bold text-slate-900 tracking-tight" data-testid={`margin-${period}-kept`}>
          {fmt(data?.admin_kept)}
        </span>
        <span className="font-mono text-sm font-semibold text-emerald-600" data-testid={`margin-${period}-take`}>
          {(Number(data?.take_pct) || 0).toFixed(1)}%
        </span>
      </div>
      <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-4">Kept · Take</p>

      <div className="space-y-2 pt-3 border-t border-slate-100">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-xs text-slate-600">
            <HandCoins size={14} className="text-[#002FA7]" weight="duotone" />
            Earned from Impact
          </span>
          <span className="font-mono text-xs font-semibold text-slate-900" data-testid={`margin-${period}-earned`}>
            {fmt(data?.impact_earned)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-xs text-slate-600">
            <Users size={14} className="text-amber-600" weight="duotone" />
            Paid to Publishers
          </span>
          <span className="font-mono text-xs font-semibold text-slate-900" data-testid={`margin-${period}-paid`}>
            {fmt(data?.pub_paid)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-xs text-slate-600 font-semibold">
            <Vault size={14} className="text-emerald-600" weight="duotone" />
            Admin Kept
          </span>
          <span className="font-mono text-xs font-bold text-emerald-700">
            {fmt(data?.admin_kept)}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-8" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-slate-400 mb-2">
            Operations / Overview
          </p>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight leading-none">
            Admin Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-2">Overview of your affiliate network</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => fetchSummary(true)} data-testid="admin-refresh-btn">
          <ArrowsClockwise size={16} className="mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {metrics.map((m) => (
          <div
            key={m.label}
            className="border border-slate-200 rounded-md p-5 bg-white hover:border-slate-300 transition-colors relative overflow-hidden"
            data-testid={`metric-${m.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <div className="absolute top-0 left-0 h-full w-0.5 bg-[#002FA7]" />
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold">{m.label}</span>
              <m.icon size={20} className={m.color} weight="duotone" />
            </div>
            <div className="text-2xl font-bold text-slate-900 font-mono tracking-tight">
              {m.value.toLocaleString()}
            </div>
          </div>
        ))}
      </div>

      <div className="mb-8" data-testid="margin-section">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-slate-400 mb-1">Margin · Take</p>
            <h2 className="font-heading text-xl font-bold text-slate-900 tracking-tight">
              Network margin snapshot
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Capped model: Impact payout minus what we owe publishers. Reversed actions excluded.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MarginCard title="Today" period="today" data={margin.today} accent="bg-emerald-500" />
          <MarginCard title="Month to date" period="mtd" data={margin.mtd} accent="bg-[#002FA7]" />
          <MarginCard title="All time" period="all-time" data={margin.all_time} accent="bg-slate-900" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-slate-200 rounded-md p-5">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="font-heading text-base font-semibold text-slate-900 tracking-tight">Actions Summary</h3>
            <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-400">Network</span>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-sm text-slate-600">Total Network Payout</span>
              <span className="font-mono text-sm font-semibold text-slate-900">${(summary?.total_payout || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-sm text-slate-600">Total Sale Amount</span>
              <span className="font-mono text-sm font-semibold text-slate-900">${(summary?.total_amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-sm text-slate-600">Pending Actions</span>
              <span className="font-mono text-sm text-amber-600">{summary?.pending || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-sm text-slate-600">Approved Actions</span>
              <span className="font-mono text-sm text-emerald-600">{summary?.approved || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-slate-600">Reversed Actions</span>
              <span className="font-mono text-sm text-red-600">{summary?.reversed || 0}</span>
            </div>
          </div>
        </div>

        <div className="border border-slate-200 rounded-md p-5">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="font-heading text-base font-semibold text-slate-900 tracking-tight">Quick Actions</h3>
            <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-400">Shortcuts</span>
          </div>
          <div className="space-y-2">
            {[
              { href: "/admin/daily-ops", title: "Daily Ops", desc: "15-min morning checklist — reports, syncs, catalog" },
              { href: "/admin/brands", title: "Sync Brands", desc: "Pull latest campaigns from network" },
              { href: "/admin/publishers", title: "Manage Publishers", desc: "Assign brands, set commissions, view SubIDs" },
              { href: "/admin/reports", title: "Sync Actions / Reports", desc: "Pull latest conversions and click data" },
            ].map((a) => (
              <a
                key={a.href}
                href={a.href}
                className="group flex items-center justify-between gap-3 p-3 border border-slate-200 rounded-md hover:border-[#002FA7] hover:bg-slate-50 transition-all"
              >
                <div>
                  <div className="text-sm font-semibold text-slate-900 group-hover:text-[#002FA7] transition-colors">
                    {a.title}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{a.desc}</div>
                </div>
                <span className="text-[#002FA7] opacity-0 group-hover:opacity-100 transition-opacity text-lg leading-none">→</span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
